import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import * as formHelpers from '../../scripts/formHelpers';
import * as formValidators from '../../scripts/formValidators';
import * as dateHelpers from '../../scripts/dateHelpers';
import * as states from '../../constants/states';
import {
  DateField,
  MoneyField,
  NumberField,
  EinField,
} from '../shared/formFields';
import PropertyAmenitiesTableContainer from '../../containers/shared/PropertyAmenitiesTableContainer';
import $ from 'jquery';
import {
  MotifButton,
  MotifModalBody,
  MotifModalFooter,
  MotifToast,
  MotifIcon,
  MotifTooltip,
} from '@ey-xd/motif-react';
import { IconoirInfoCircle } from '@ey-xd/motif-icon';
import { EditPropertyFields } from './EditPropertyForm';
import { addAttribute } from '../../common/Attribute';
import { useSelector } from 'react-redux';
import * as formParsers from '../../scripts/formParsers';
import { normalizeReportPeriodObjects } from '../../common/ReportPeriodUtils';

const EditReitForm = ({
  handleSubmit,
  handleCloseForm,
  initialValues,
  reitFormPurpose,
  showReitSuccess,
  error,
  submitting,
  handleDismissErrorMessage,
  isTestingEnabled,
  onSubmit,
  fields,
  lookupTypes,
  isAudit,
  groupsData,
}) => {
  const [hideInfoMessage, setHideInfoMessage] = useState(false);
  const featureFlags = useSelector((state) => state.featureManagementData);
  useEffect(() => {
    //Add Attribute(type=button) to all button elements having attribute(role = 'combobox')
    addAttribute("button[role = 'combobox']", 'type', 'button');
  });

  const options = lookupTypes.psqPropertyTypes
    .filter((ppt) => ppt.psqPropertyTypeID !== 0)
    .map((option) => {
      const psqOption = {};
      psqOption.key = option.psqPropertyTypeID;
      psqOption.value = option.psqPropertyTypeDescription;
      psqOption.text = option.psqPropertyTypeDescription;
      return psqOption;
    });

  let submitButtonText = 'Submit';
  if (reitFormPurpose === 'EDIT') {
    submitButtonText = 'Save';
  }

  let scrollTop = () => {
    $('.ReactModal__Content').scrollTop(0);
  };

  let save = (e) => {
    e.preventDefault();
    scrollTop();

    handleSubmit((values) => onSubmit({ ...values, anotherProperty: 'NO' }))();
  };

  let saveAndCreateAnotherProperty = (e) => {
    e.preventDefault();
    scrollTop();

    handleSubmit((values) => onSubmit({ ...values, anotherProperty: 'YES' }))();
  };

  const currentYear = new Date().getFullYear();
  let yearOptions = [];
  const dropdownStartYear = isAudit == true ? currentYear - 1 : currentYear - 5;
  let initialYear =
    featureFlags && featureFlags['RestrictedReportPeriodLimit']
      ? dropdownStartYear
      : currentYear - 5;

  for (let i = initialYear; i <= currentYear + 10; i++) {
    yearOptions.push(i.toString());
  }

  const quarters = yearOptions.flatMap((year) => {
    return [1, 2, 3, 4].map((q) => {
      return {
        key: `Q${q} ${year}`,
        value: `Q${q} ${year}`,
        text: `Q${q} ${year}`,
      };
    });
  });

  // For edit mode, if initial quarter is very old quarter, prior to 2020
  // If such initial quarter is not exists then add to quarter list to display in non editable select dropdown.
  if (
    reitFormPurpose === 'EDIT' &&
    quarters &&
    initialValues &&
    !quarters.some((quarter) => quarter.key === initialValues.initialQuarter)
  ) {
    quarters.push({
      key: initialValues.initialQuarter,
      value: initialValues.initialQuarter,
      text: initialValues.initialQuarter,
    });
  }

  const [showPsqTypes, setShowPsqTypes] = useState(true);

  // Normalize and sort report period objects by 'text' column,
  // using ascending order for both year and quarter
  const quarterData = normalizeReportPeriodObjects(
    quarters,
    'text',
    'asc',
    'asc'
  );

  const rerenderPsqTypes = () => {
    setShowPsqTypes(false);
    setTimeout(() => setShowPsqTypes(true));
  };

  return (
    <>
      <MotifModalBody>
        <form onSubmit={handleSubmit} className="reit" id="reitForm">
          <Field
            name="reitid"
            component={formHelpers.renderField}
            type="hidden"
          />
          <section className="reit">
            {formHelpers.showSubmissionError(error, handleDismissErrorMessage)}

            <div className="row">
              <div className="field col-md-6" style={{ minHeight: '44px' }}>
                <Field
                  name="entityGroupID"
                  label="Group Name"
                  placeholder="Select a Group"
                  component={formHelpers.SingleSelectComponent}
                  type="select"
                  options={groupsData}
                  optionValue={(o) => o.entityGroupID}
                  optionText={(o) => o.entityGroupName}
                  testingData={{ label: 'lblGroupName', div: 'divGroupName' }}
                ></Field>
              </div>
            </div>
            <div className="row">
              <div className="field col-md-3">
                <Field
                  name="reitTrackingID"
                  component={formHelpers.renderField}
                  type="text"
                  label="REITSuite Entity ID"
                  maxLength={50}
                  disabled={true}
                  validate={[
                    formValidators.minLength2,
                    formValidators.maxLength50,
                    formValidators.noWindowSpecialChars,
                  ]}
                  dataTestId="txtREITSuiteEntityId"
                />
              </div>
              <div className="field col-md-3">
                <Field
                  name="gtpEntityID"
                  component={formHelpers.renderField}
                  type="text"
                  label="GTP Entity ID"
                  maxLength={50}
                  validate={[formValidators.maxLength50]}
                  dataTestId="txtGTPEntityId"
                />
              </div>
              <div className="field col-md-3">
                <Field
                  name="clientEntityID"
                  component={formHelpers.renderField}
                  type="text"
                  label="Client Entity ID"
                  maxLength={50}
                  validate={[formValidators.maxLength50]}
                  dataTestId="txtClientEntityId"
                />
              </div>
              <div className="field col-md-3">
                <EinField
                  name="ein"
                  label="EIN"
                  placeholder="XX-XXXXXXX"
                  isRequiredField={true}
                  validate={[
                    formValidators.required,
                    formValidators.maxLength50,
                    formValidators.ein,
                  ]}
                  dataTestId="txtEIN"
                />
              </div>
            </div>

            <div className="row">
              <div className="field col-md-6">
                <Field
                  name="reitName"
                  component={formHelpers.renderField}
                  type="text"
                  label="Full Name"
                  isRequiredField={true}
                  maxLength={50}
                  validate={[
                    formValidators.required,
                    formValidators.minLength2,
                    formValidators.maxLength50,
                    formValidators.noWindowSpecialChars,
                  ]}
                  dataTestId="txtFullName"
                />
              </div>
              <div className="field col-md-6">
                <Field
                  name="reitShortName"
                  component={formHelpers.renderField}
                  type="text"
                  label="Short Name"
                  maxLength={50}
                  isRequiredField={true}
                  validate={[
                    formValidators.required,
                    formValidators.minLength2,
                    formValidators.maxLength50,
                  ]}
                  dataTestId="txtShortName"
                />
              </div>
            </div>

            <div className="row">
              <div className="field col-md-3">
                <Field
                  name="symbol"
                  component={formHelpers.renderField}
                  type="text"
                  label="REIT Symbol"
                  maxLength={50}
                  validate={[formValidators.maxLength50]}
                  dataTestId="txtREITSymbol"
                />
              </div>
              <div className="field col-md-3">
                <Field
                  name="reitTypeID"
                  label="REIT Type (Required)"
                  placeholder="Select a Type"
                  component={formHelpers.SingleSelectComponent}
                  type="select"
                  options={lookupTypes.reitTypes}
                  optionValue={(o) => o.reitTypeID}
                  optionText={(o) => o.reitTypeDescription}
                  validate={[formValidators.requiredSelect]}
                  testingData={{ label: 'lblREITType', div: 'divREITType' }}
                ></Field>
              </div>
              <div className="field col-md-3">
                <div>
                  <Field
                    name="organizationTypeID"
                    label="Organization Type (Required)"
                    placeholder="Select an Organization Type"
                    component={formHelpers.SingleSelectComponent}
                    type="select"
                    options={lookupTypes.organizationTypes.filter(
                      (o) => o.organizationTypeID !== 0
                    )}
                    optionValue={(o) => o.organizationTypeID}
                    optionText={(o) => o.organizationTypeDescription}
                    validate={[formValidators.requiredSelect]}
                    testingData={{ label: 'lblOrgType', div: 'divOrgType' }}
                  />
                </div>
              </div>
              <div className="field col-md-3">
                <Field
                  name="stateOrganized"
                  label="State Organized In (Required)"
                  placeholder="Select a State"
                  component={formHelpers.SingleSelectComponent}
                  type="select"
                  options={states.usStates}
                  optionValue={(s) => s.value}
                  optionText={(s) => s.label}
                  validate={[formValidators.requiredSelect]}
                  testingData={{ label: 'lblStOrgIn', div: 'divStOrgIn' }}
                />
              </div>
            </div>
            <div className="row">
              {!hideInfoMessage &&
                showPsqTypes &&
                reitFormPurpose === 'EDIT' && (
                  <div className="field col-md-12">
                    <MotifToast
                      className="mb-3"
                      variant="error"
                      onClose={setHideInfoMessage}
                      data-testid="reitTypeToastMsg"
                    >
                      Warning: Changing the REIT Type will change the default
                      ARQC assigned to this REIT on the checklist auto delivery
                      calendar.
                    </MotifToast>
                  </div>
                )}
            </div>
            <div className="row">
              <div className="field col-md-3">
                <DateField
                  name="formationDate"
                  label="Formation Date"
                  placeholder="MM/DD/YYYY"
                  isRequiredField={true}
                  validate={[
                    formValidators.required,
                    formValidators.isValidDate,
                  ]}
                  dataTestId="txtFormationDt"
                />
              </div>
              <div className="field col-md-3">
                <DateField
                  name="dateOfElection"
                  label="Date of REIT Election"
                  isRequiredField={true}
                  validate={[
                    formValidators.required,
                    formValidators.isValidDate,
                  ]}
                  dataTestId="txtREITElectDt"
                />
              </div>
              <div className="field col-md-5">
                <div className="row">
                  <div className="col-sm-6" style={{ paddingRight: 0 }}>
                    <Field
                      name="initialQuarter"
                      label="Initial Quarter"
                      placeholder="Quarter"
                      component={formHelpers.SingleSelectComponent}
                      type="select"
                      options={quarterData}
                      optionValue={(q) => q.value}
                      optionText={(q) => q.text}
                      optionKey={(r) => r.key}
                      disabled={reitFormPurpose === '' ? false : true}
                      testingData={{
                        label: 'lblInitialQuarter',
                        div: 'divInitialQuarter',
                      }}
                    />
                  </div>
                  <div
                    className="col-sm-6"
                    style={{ paddingLeft: '6px', marginTop: '11px' }}
                  >
                    <MotifTooltip
                      placement="right"
                      trigger={
                        <MotifIcon
                          iconFunction={IconoirInfoCircle}
                          size="24"
                          data-testid="InfoInitialQuarter"
                        />
                      }
                    >
                      <div>
                        This is to establish the report period for the
                        <br />
                        REIT during its initial creation only. To make
                        <br />
                        updates go to 'Bulk Period Creation' page.
                      </div>
                    </MotifTooltip>
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="field col-md-3">
                <NumberField
                  name="percentageThreshold"
                  label="Percentage Threshold"
                  placeholder="0.00"
                  allowDecimal={true}
                  isRequiredField={true}
                  maxLength={19}
                  validate={[
                    formValidators.required,
                    formValidators.number,
                    formValidators.maxValueDecimal,
                  ]}
                  parse={formParsers.positiveNumberParser}
                  dataTestId="txtPercentageThreshold"
                />
              </div>
              <div className="field col-md-3">
                <MoneyField
                  name="balanceThreshold"
                  label="Balance Threshold"
                  isRequiredField={true}
                  allowDecimal={true}
                  validate={[
                    formValidators.required,
                    formValidators.maxValueDecimal,
                    formValidators.number,
                  ]}
                  dataTestId="txtBalanceThreshold"
                />
              </div>
              <div className="field col-md-3">
                <NumberField
                  name="numberOfSharesClasses"
                  label="# Shares Classes"
                  placeholder="0"
                  allowDecimal={true}
                  parse={formParsers.positiveWholeNumberParser} // add this parse to prevent accepting negative (-) value and decimal
                  validate={[
                    formValidators.number,
                    formValidators.maxValueInt,
                    formValidators.isWholeNumber,
                  ]}
                  dataTestId="txtShareClass"
                />
              </div>
              <div className="field col-md-3">
                <NumberField
                  name="preferredNumberOfShareHolders"
                  allowDecimal={true}
                  label="# Preferred ShareHolders"
                  placeholder="0"
                  validate={[
                    formValidators.number,
                    formValidators.maxValueInt,
                    formValidators.isWholeNumber,
                  ]}
                  parse={formParsers.positiveWholeNumberParser} // add this parse to prevent accepting negative (-) value
                  dataTestId="txtPreferedShareholders"
                />
              </div>
            </div>
          </section>
        </form>
      </MotifModalBody>
      <MotifModalFooter>
        <MotifButton
          variant="secondary"
          type="button"
          onClick={handleCloseForm}
          form="reitForm"
          data-testid="btnCancel"
        >
          Cancel
        </MotifButton>
        <MotifButton
          variant="primary"
          type="submit"
          onClick={save}
          disabled={submitting}
          form="reitForm"
          data-testid="btnSubmit"
        >
          {submitButtonText}
        </MotifButton>
      </MotifModalFooter>
    </>
  );
};

EditReitForm.propTypes = {
  lookupTypes: PropTypes.object,
  handleSubmit: PropTypes.func.isRequired,
  handleCloseForm: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  error: PropTypes.object,
  submitting: PropTypes.bool,
  reitFormPurpose: PropTypes.string,
  showReitSuccess: PropTypes.bool,
  handleDismissErrorMessage: PropTypes.func,
  isTestingEnabled: PropTypes.bool,
  fields: PropTypes.array,
  onSubmit: PropTypes.func.isRequired,
  clientIsAudit: PropTypes.bool,
};

export default reduxForm({ form: 'reit' })(EditReitForm);
